import { Progress } from "@heroui/react";
import { LuCheck } from "react-icons/lu";
import { ThemedText } from "@/components/ui";

/**
 * Stepper - Reusable multi-step progress component
 *
 * @param {Array} steps - Array of step objects { id, title, icon }
 * @param {number} currentStep - Current active step number
 * @param {string} className - Additional classes for wrapper
 */
const Stepper = ({ steps, currentStep, className = "" }) => {
    // Determine progress bar percentage for the bottom bar
    const progressPercent = (currentStep / steps.length) * 100;

    return (
        <div className={className}>
            <div className="relative flex w-full items-start justify-between">
                {steps.map((step, index) => {
                    const Icon = step.icon;
                    const isActive = currentStep === step.id;
                    const isCompleted = currentStep > step.id;
                    const isFirst = index === 0;
                    const isLast = index === steps.length - 1;

                    return (
                        <div
                            key={step.id}
                            className="relative flex flex-1 flex-col items-center"
                        >
                            {/* Left Line (connects to previous) */}
                            {/* Hidden on first step. MR-8 creates gap from center icon */}
                            {!isFirst && (
                                <div
                                    className={`absolute top-5 right-1/2 left-0 mr-8 h-0.5 -translate-y-1/2 ${
                                        isActive || isCompleted
                                            ? "bg-primary"
                                            : "bg-default-200"
                                    }`}
                                />
                            )}

                            {/* Right Line (connects to next) */}
                            {/* Hidden on last step. ML-8 creates gap from center icon */}
                            {!isLast && (
                                <div
                                    className={`absolute top-5 right-0 left-1/2 ml-8 h-0.5 -translate-y-1/2 ${
                                        isCompleted
                                            ? "bg-primary"
                                            : "bg-default-200"
                                    }`}
                                />
                            )}

                            {/* Step Indicator */}
                            <div
                                className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all ${
                                    isActive
                                        ? "border-primary bg-primary text-white"
                                        : isCompleted
                                          ? "border-primary bg-primary text-white"
                                          : "border-default-200 text-default-400 dark:bg-default-50 bg-white"
                                }`}
                            >
                                {isCompleted ? (
                                    <LuCheck size={20} />
                                ) : (
                                    <Icon size={20} />
                                )}
                            </div>

                            {/* Title */}
                            <ThemedText
                                size="sm"
                                className={`mt-2 text-center font-medium ${
                                    isActive
                                        ? "text-primary"
                                        : isCompleted
                                          ? "text-success"
                                          : "text-default-500"
                                }`}
                            >
                                {step.title}
                            </ThemedText>
                        </div>
                    );
                })}
            </div>

            {/* Global Progress Bar at bottom */}
            <Progress
                value={progressPercent}
                size="sm"
                color="primary"
                className="mt-6"
                aria-label="Loading..."
            />
        </div>
    );
};

export default Stepper;
